
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings } from "lucide-react";
import { toast } from "sonner";

interface AnalyticsSettingsProps {
  settings: AnalyticsConfig;
  onSettingsChange: (settings: AnalyticsConfig) => void;
}

export interface AnalyticsConfig {
  showKPIs: boolean;
  showSourceAnalytics: boolean;
  showTimeToHire: boolean;
  showRevenueAnalytics: boolean;
  showRecruiterPerformance: boolean;
  defaultDateRange: string;
  refreshInterval: number;
  chartTheme: 'light' | 'dark' | 'auto';
}

export const AnalyticsSettings = ({ settings, onSettingsChange }: AnalyticsSettingsProps) => {
  const [localSettings, setLocalSettings] = useState<AnalyticsConfig>(settings);
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = () => {
    onSettingsChange(localSettings);
    toast.success("Analytics settings saved");
    setIsOpen(false);
  };

  const handleReset = () => {
    const defaultSettings: AnalyticsConfig = {
      showKPIs: true,
      showSourceAnalytics: true,
      showTimeToHire: true,
      showRevenueAnalytics: true,
      showRecruiterPerformance: true,
      defaultDateRange: '6m',
      refreshInterval: 300000, // 5 minutes
      chartTheme: 'auto'
    };
    setLocalSettings(defaultSettings);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Settings
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Analytics Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Dashboard Sections */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Dashboard Sections</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="show-kpis">Key Performance Indicators</Label>
                <Switch
                  id="show-kpis"
                  checked={localSettings.showKPIs}
                  onCheckedChange={(checked) => 
                    setLocalSettings(prev => ({ ...prev, showKPIs: checked }))
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="show-source">Source Analytics</Label>
                <Switch
                  id="show-source"
                  checked={localSettings.showSourceAnalytics}
                  onCheckedChange={(checked) => 
                    setLocalSettings(prev => ({ ...prev, showSourceAnalytics: checked }))
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="show-time">Time to Hire</Label>
                <Switch
                  id="show-time"
                  checked={localSettings.showTimeToHire}
                  onCheckedChange={(checked) => 
                    setLocalSettings(prev => ({ ...prev, showTimeToHire: checked }))
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="show-revenue">Revenue Analytics</Label>
                <Switch
                  id="show-revenue"
                  checked={localSettings.showRevenueAnalytics}
                  onCheckedChange={(checked) => 
                    setLocalSettings(prev => ({ ...prev, showRevenueAnalytics: checked }))
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="show-performance">Recruiter Performance</Label>
                <Switch
                  id="show-performance"
                  checked={localSettings.showRecruiterPerformance}
                  onCheckedChange={(checked) => 
                    setLocalSettings(prev => ({ ...prev, showRecruiterPerformance: checked }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Default Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Default Settings</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="default-range">Default Date Range</Label>
                <Select
                  value={localSettings.defaultDateRange}
                  onValueChange={(value) => 
                    setLocalSettings(prev => ({ ...prev, defaultDateRange: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="3m">Last 3 months</SelectItem>
                    <SelectItem value="6m">Last 6 months</SelectItem>
                    <SelectItem value="1y">Last year</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="chart-theme">Chart Theme</Label>
                <Select
                  value={localSettings.chartTheme}
                  onValueChange={(value: 'light' | 'dark' | 'auto') => 
                    setLocalSettings(prev => ({ ...prev, chartTheme: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleReset}>
            Reset to Defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Settings
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
